import { useState, useEffect, useRef } from "react";
import { api, authHeaders, handleAuthFailure } from "../data/api";
import Badge from "../components/Badge";
import Pill from "../components/Pill";
import Btn from "../components/Btn";
import StatusToggle from "../components/StatusToggle";
import { TableWrap, TH, TD } from "../components/TableWrap";
import { useTheme } from "../context/ThemeContext";
import { Eye, Pencil } from "lucide-react";

const EMPTY_FORM = {
  id: "",
  display_name: "",
  source_ip: "",
  source_port: "",
  channel_ip: "",
  quality: "SD",
  bandwidth: "",
  channel_number: "",
  video_bitrate: "",
  audio1_bitrate: "",
  audio2_bitrate: "",
};

const T = {
  textDim: "var(--text-dim)",
  surface: "var(--surface)",
  border: "var(--border)",
  text: "var(--text)",
  textSub: "var(--text-sub)",
};

function validate(form) {
  if (!form.id.trim()) return "Channel ID is required";
  if (!form.display_name.trim()) return "Display name is required";
  if (!form.source_ip.trim()) return "Source IP is required";
  if (!form.source_port.trim()) return "Port number is required";
  if (
    isNaN(Number(form.source_port)) ||
    Number(form.source_port) < 1 ||
    Number(form.source_port) > 65535
  )
    return "Port must be between 1 and 65535";
  return null;
}

// Inline editable cell
function EditCell({ value, onChange, mono, placeholder }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder || "—"}
      style={{
        width: "100%",
        background: "var(--surface-alt)",
        border: `1px solid var(--border)`,
        borderRadius: 5,
        padding: "5px 8px",
        fontSize: 12,
        color: "var(--text)",
        fontFamily: mono ? "'IBM Plex Mono', monospace" : "inherit",
        outline: "none",
        boxSizing: "border-box",
      }}
    />
  );
}

function NewChannelPage({ onBack, onCreated }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const f = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleCreate = async () => {
    const err = validate(form);
    if (err) {
      setError(err);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const created = await api.channels.create({
        id: form.id.trim(),
        display_name: form.display_name.trim(),
        source_ip: form.source_ip.trim(),
        source_port: Number(form.source_port),
        channel_ip: form.channel_ip.trim() || null,
        quality: form.quality,
        bandwidth: form.bandwidth.trim() || null,
        channel_number: form.channel_number
          ? Number(form.channel_number)
          : null,
        video_bitrate: form.video_bitrate ? Number(form.video_bitrate) : null,
        audio1_bitrate: form.audio1_bitrate
          ? Number(form.audio1_bitrate)
          : null,
        audio2_bitrate: form.audio2_bitrate
          ? Number(form.audio2_bitrate)
          : null,
      });
      onCreated(created.id);
    } catch (e) {
      setError(e.message || "Something went wrong.");
      setSubmitting(false);
    }
  };

  const previewUrl =
    form.source_ip && form.source_port
      ? `srt://${form.source_ip}:${form.source_port}`
      : null;

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 18,
          paddingBottom: 14,
          borderBottom: `1px solid var(--border)`,
        }}
      >
        <h1 style={{ fontSize: 17, fontWeight: 700, color: "var(--ink)" }}>
          New Channel
        </h1>
        <Btn onClick={onBack}>← Back to Channels</Btn>
      </div>

      {error && (
        <div
          style={{
            background: "var(--red-bg)",
            border: `1px solid var(--red-border)`,
            borderRadius: 7,
            padding: "10px 14px",
            marginBottom: 16,
            fontSize: 12,
            color: "var(--red)",
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          background: "var(--surface)",
          border: `1px solid var(--border)`,
          borderRadius: 9,
          padding: 24,
          maxWidth: 600,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}
          >
            <div>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--text-dim)",
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                  fontWeight: 600,
                  marginBottom: 5,
                }}
              >
                Channel ID
              </div>
              <input
                value={form.id}
                onChange={f("id")}
                style={{
                  width: "100%",
                  background: "var(--surface)",
                  border: `1px solid var(--border)`,
                  borderRadius: 6,
                  padding: "7px 10px",
                  fontSize: 12,
                  color: "var(--text)",
                  fontFamily: "'IBM Plex Mono', monospace",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--text-dim)",
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                  fontWeight: 600,
                  marginBottom: 5,
                }}
              >
                Display Name
              </div>
              <input
                value={form.display_name}
                onChange={f("display_name")}
                style={{
                  width: "100%",
                  background: "var(--surface)",
                  border: `1px solid var(--border)`,
                  borderRadius: 6,
                  padding: "7px 10px",
                  fontSize: 12,
                  color: "var(--text)",
                  fontFamily: "inherit",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>

          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--text-dim)",
                textTransform: "uppercase",
                letterSpacing: "0.07em",
                marginBottom: 8,
              }}
            >
              Stream Source
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 140px",
                gap: 10,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--text-dim)",
                    marginBottom: 5,
                  }}
                >
                  Source IP
                </div>
                <input
                  value={form.source_ip}
                  onChange={f("source_ip")}
                  style={{
                    width: "100%",
                    background: "var(--surface)",
                    border: `1px solid var(--border)`,
                    borderRadius: 6,
                    padding: "7px 10px",
                    fontSize: 12,
                    color: "var(--text)",
                    fontFamily: "'IBM Plex Mono', monospace",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--text-dim)",
                    marginBottom: 5,
                  }}
                >
                  Port
                </div>
                <input
                  value={form.source_port}
                  onChange={f("source_port")}
                  style={{
                    width: "100%",
                    background: "var(--surface)",
                    border: `1px solid var(--border)`,
                    borderRadius: 6,
                    padding: "7px 10px",
                    fontSize: 12,
                    color: "var(--text)",
                    fontFamily: "'IBM Plex Mono', monospace",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </div>
            {previewUrl && (
              <div
                style={{ marginTop: 6, fontSize: 11, color: "var(--text-sub)" }}
              >
                SRT URL:{" "}
                <span
                  style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    color: "var(--ink)",
                  }}
                >
                  {previewUrl}
                </span>
              </div>
            )}
          </div>

          {/* <div>
            <div style={{ fontSize:11, color:"var(--text-dim)", textTransform:'uppercase', letterSpacing:'0.07em', fontWeight:600, marginBottom:5 }}>Channel Source IP <span style={{ fontWeight:400 }}>(optional)</span></div>
            <input value={form.channel_ip} onChange={f('channel_ip')} placeholder="10.0.0.50" style={{ width:'100%', background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:6, padding:'7px 10px', fontSize:12, color:"var(--text)", fontFamily:"'IBM Plex Mono', monospace", outline:'none', boxSizing:'border-box' }} />
          </div> */}

          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}
          >
            <div>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--text-dim)",
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                  fontWeight: 600,
                  marginBottom: 5,
                }}
              >
                Quality
              </div>
              <div
                style={{
                  display: "flex",
                  border: `1px solid var(--border)`,
                  borderRadius: 6,
                  overflow: "hidden",
                }}
              >
                {["SD", "HD"].map((q) => (
                  <button
                    key={q}
                    onClick={() => setForm((p) => ({ ...p, quality: q }))}
                    style={{
                      flex: 1,
                      padding: "7px 0",
                      fontSize: 12,
                      fontWeight: form.quality === q ? 600 : 400,
                      background:
                        form.quality === q ? "var(--ink)" : "var(--surface)",
                      color: form.quality === q ? "#fff" : "var(--text-sub)",
                      border: "none",
                      cursor: "pointer",
                      fontFamily: "inherit",
                      transition: "all 0.12s",
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}
          >
            <div>
              <div
                style={{
                  fontSize: 11,
                  color: T.textDim,
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                  fontWeight: 600,
                  marginBottom: 5,
                }}
              >
                Channel Number
              </div>
              <input
                value={form.channel_number}
                onChange={f("channel_number")}
                style={{
                  width: "100%",
                  background: T.surface,
                  border: `1px solid ${T.border}`,
                  borderRadius: 6,
                  padding: "7px 10px",
                  fontSize: 12,
                  color: T.text,
                  fontFamily: "'IBM Plex Mono', monospace",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div>
              <div
                style={{
                  fontSize: 11,
                  color: T.textDim,
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                  fontWeight: 600,
                  marginBottom: 5,
                }}
              >
                Video Bit Rate (Mbps)
              </div>
              <input
                value={form.video_bitrate}
                onChange={f("video_bitrate")}
                style={{
                  width: "100%",
                  background: T.surface,
                  border: `1px solid ${T.border}`,
                  borderRadius: 6,
                  padding: "7px 10px",
                  fontSize: 12,
                  color: T.text,
                  fontFamily: "'IBM Plex Mono', monospace",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>

          {/* Audio 1 + Audio 2 */}
          {/* <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}
          >
            <div>
              <div
                style={{
                  fontSize: 11,
                  color: T.textDim,
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                  fontWeight: 600,
                  marginBottom: 5,
                }}
              >
                Audio 1 (Kbps)
              </div>
              <input
                value={form.audio1_bitrate}
                onChange={f("audio1_bitrate")}
                style={{
                  width: "100%",
                  background: T.surface,
                  border: `1px solid ${T.border}`,
                  borderRadius: 6,
                  padding: "7px 10px",
                  fontSize: 12,
                  color: T.text,
                  fontFamily: "'IBM Plex Mono', monospace",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div>
              <div
                style={{
                  fontSize: 11,
                  color: T.textDim,
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                  fontWeight: 600,
                  marginBottom: 5,
                }}
              >
                Audio 2 (Kbps){" "}
                <span style={{ fontWeight: 400 }}>(optional)</span>
              </div>
              <input
                value={form.audio2_bitrate}
                onChange={f("audio2_bitrate")}
                style={{
                  width: "100%",
                  background: T.surface,
                  border: `1px solid ${T.border}`,
                  borderRadius: 6,
                  padding: "7px 10px",
                  fontSize: 12,
                  color: T.text,
                  fontFamily: "'IBM Plex Mono', monospace",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div> */}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 16,
          maxWidth: 600,
        }}
      >
        <Btn onClick={onBack}>Cancel</Btn>
        <Btn primary onClick={handleCreate} disabled={submitting}>
          {submitting ? "Creating…" : "Create Channel"}
        </Btn>
      </div>
    </>
  );
}

function ChannelDetailsModal({ channel, onClose, onSaved, readOnly }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm]       = useState(null);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);

  if (!channel) return null;

  const startEdit = () => {
    setForm({
      display_name: channel.display_name || "",
      channel_number: channel.channel_number ? String(channel.channel_number) : "",
      source_ip: channel.source_ip || "",
      source_port: channel.source_port ? String(channel.source_port) : "",
      channel_ip: channel.channel_ip || "",
      quality: channel.quality || "SD",
      bandwidth: channel.bandwidth || "",
      video_bitrate: channel.video_bitrate ? String(channel.video_bitrate) : "",
      audio1_bitrate: channel.audio1_bitrate ? String(channel.audio1_bitrate) : "",
      audio2_bitrate: channel.audio2_bitrate ? String(channel.audio2_bitrate) : "",
    });
    setError(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setForm(null);
    setError(null);
  };

  const f = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    if (!form.display_name.trim()) {
      setError("Display name is required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await api.channels.update(channel.id, {
        display_name: form.display_name.trim(),
        channel_number: form.channel_number ? Number(form.channel_number) : null,
        source_ip: form.source_ip.trim() || null,
        source_port: form.source_port ? Number(form.source_port) : null,
        channel_ip: form.channel_ip.trim() || null,
        quality: form.quality,
        bandwidth: form.bandwidth.trim() || null,
        video_bitrate: form.video_bitrate ? Number(form.video_bitrate) : null,
        audio1_bitrate: form.audio1_bitrate ? Number(form.audio1_bitrate) : null,
        audio2_bitrate: form.audio2_bitrate ? Number(form.audio2_bitrate) : null,
      });
      setEditing(false);
      setForm(null);
      if (onSaved) onSaved(updated); // lets the parent refresh its list AND this modal's displayed channel
    } catch (e) {
      setError(e.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: "100%", boxSizing: "border-box", background: "var(--surface)",
    border: `1px solid var(--border)`, borderRadius: 6, padding: "5px 8px",
    fontSize: 12, color: "var(--text)", fontFamily: "'IBM Plex Mono', monospace", outline: "none",
  };

  // [label, key-or-null (null = not editable), formatted display value]
  const rows = [
    ["Channel ID", null, channel.id],
    ["Channel No.", "channel_number", channel.channel_number ?? "—"],
    ["Display Name", "display_name", channel.display_name],
    ["Source IP", "source_ip", channel.source_ip || "—"],
    ["Source Port", "source_port", channel.source_port ?? "—"],
    ["Channel IP", "channel_ip", channel.channel_ip || "—"],
    ["Master SRT URL", null, channel.master_srt_url || "—"],
    ["Quality", "quality", channel.quality || "SD"],
    ["Video Bitrate", "video_bitrate", channel.video_bitrate != null ? `${channel.video_bitrate} Mbps` : "—"],
    ["Audio 1 Bitrate", "audio1_bitrate", channel.audio1_bitrate != null ? `${channel.audio1_bitrate} kbps` : "—"],
    ["Audio 2 Bitrate", "audio2_bitrate", channel.audio2_bitrate != null ? `${channel.audio2_bitrate} kbps` : "—"],
    ["Status", null, channel.status],
    ["Affiliates", null, channel.affiliates ?? "—"],
    ["Devices", null, channel.devices ?? "—"],
    ["Promos", null, channel.promo_count ?? "—"],
    ["Created", null, channel.created_at ? new Date(channel.created_at).toLocaleString() : "—"],
    ["Last Updated", null, channel.updated_at ? new Date(channel.updated_at).toLocaleString() : "—"],
  ];

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 300, fontFamily: "'DM Sans', system-ui, sans-serif" }}
    >
      <div style={{ background: "var(--surface)", border: `1px solid var(--border)`, borderRadius: 12, width: 480, maxHeight: "82vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid var(--border)`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)" }}>{channel.display_name}</div>
            <div style={{ fontSize: 11, color: "var(--text-dim)", fontFamily: "'IBM Plex Mono', monospace", marginTop: 2 }}>{channel.id}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {!readOnly && !editing && (
              <button onClick={startEdit} style={{ display: "flex", alignItems: "center", gap: 5, background: "var(--surface)", border: `1px solid var(--border)`, borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: 600, color: "var(--text-sub)", cursor: "pointer", fontFamily: "inherit" }}>
                <Pencil size={13} /> Edit
              </button>
            )}
            <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 18, color: "var(--text-dim)", cursor: "pointer" }}>×</button>
          </div>
        </div>

        {error && (
          <div style={{ margin: "12px 20px 0", background: "var(--red-bg, #fef2f2)", border: "1px solid var(--red-border, #fecaca)", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "var(--red, #dc2626)" }}>
            {error}
          </div>
        )}

        <div style={{ padding: "8px 0", overflowY: "auto" }}>
          {rows.map(([label, key, displayValue]) => (
            <div key={label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "8px 20px", fontSize: 12, borderBottom: `1px solid var(--border)` }}>
              <span style={{ color: "var(--text-dim)", fontWeight: 600, flexShrink: 0 }}>{label}</span>
              {editing && key ? (
                key === "quality" ? (
                  <select value={form.quality} onChange={f("quality")} style={{ ...inputStyle, maxWidth: 220, cursor: "pointer" }}>
                    <option value="SD">SD</option>
                    <option value="HD">HD</option>
                  </select>
                ) : (
                  <input
                    value={form[key]}
                    onChange={f(key)}
                    style={{ ...inputStyle, maxWidth: 220 }}
                  />
                )
              ) : (
                <span style={{ color: "var(--ink)", fontFamily: "'IBM Plex Mono', monospace", textAlign: "right" }}>{displayValue}</span>
              )}
            </div>
          ))}
        </div>

        {editing && (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "12px 20px", borderTop: `1px solid var(--border)`, flexShrink: 0 }}>
            <button onClick={cancelEdit} style={{ background: "var(--surface)", border: `1px solid var(--border)`, borderRadius: 6, padding: "7px 14px", fontSize: 12, color: "var(--text-sub)", cursor: "pointer", fontFamily: "inherit" }}>
              Cancel
            </button>
            <button onClick={save} disabled={saving} style={{ background: "var(--ink)", border: "none", borderRadius: 6, padding: "7px 16px", fontSize: 12, fontWeight: 600, color: "#fff", cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1, fontFamily: "inherit" }}>
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ChannelManagement({ onOpenPromo, promoCounts = {}, setPromoCounts, readOnly = false }) {
  const { mode } = useTheme();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [urlFilter, setUrlFilter] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [viewingChannel, setViewingChannel] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // ── Promo upload state ──────────────────────────────────────────────────
  // promoJobs: { [jobId]: { channelId, channelName, filename, status, message } }
  const [promoJobs, setPromoJobs] = useState({});
  const [promoPanelOpen, setPromoPanelOpen] = useState(true);
  const [promoPanelMinimized, setPromoPanelMinimized] = useState(false);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [promoDragOver, setPromoDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const uploadingForChannel = useRef(null); // tracks which channel triggered the file picker
  const pollTimers = useRef({});            // stores setInterval ids per jobId
  const dismissedIds = useRef(new Set());   // promo ids the user dismissed — don't re-add from poll
  const hadProcessing = useRef(false);      // to refresh channel counts when the last job finishes

  const hasPromoJobs = Object.keys(promoJobs).length > 0;

  const PROMO_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.channels.list();
      setChannels(data);
      // Seed promo counts from API response (promo_count included in GET /)
      if (setPromoCounts) {
        const counts = {};
        data.forEach(ch => { counts[ch.id] = parseInt(ch.promo_count || 0); });
        setPromoCounts(counts);
      }
    } catch (e) {
      if (handleAuthFailure(e)) return;
      setError("Failed to load channels.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Rebuild the upload panel from server state so in-flight transcodes survive
  // navigation: poll /promos/active (processing + recently-failed) and reconcile
  // into promoJobs. A tracked 'processing' entry that drops out of the active
  // set has finished → mark it success (and refresh channel counts once).
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await fetch(`${PROMO_BASE}/channels/promos/active`, { headers: authHeaders() });
        if (!res.ok || cancelled) return;
        const { promos = [] } = await res.json();
        const activeIds = new Set(promos.map(p => p.id));

        setPromoJobs(prev => {
          const next = { ...prev };
          // Upsert server-active promos (skip ones the user dismissed).
          promos.forEach(p => {
            if (dismissedIds.current.has(p.id)) return;
            const ex = next[p.id] || {};
            next[p.id] = {
              ...ex,
              promoId:     p.id,
              channelId:   p.channel_id,
              channelName: ex.channelName || p.channel_name || p.channel_id,
              filename:    ex.filename || p.original_name || p.filename,
              status:      p.status === 'failed' ? 'failed' : 'processing',
              message:     p.status === 'failed' ? (ex.message || 'Processing failed') : null,
              objectUrl:   ex.objectUrl,
            };
          });
          // Tracked processing entries no longer active → completed.
          Object.keys(next).forEach(k => {
            const j = next[k];
            if (j.status === 'processing' && j.promoId && !activeIds.has(j.promoId)) {
              next[k] = { ...j, status: 'success', message: null };
            }
          });
          return next;
        });

        const nowProcessing = promos.some(p => p.status === 'processing');
        if (hadProcessing.current && !nowProcessing) load(); // refresh promo counts
        hadProcessing.current = nowProcessing;
      } catch { /* keep polling */ }
    };
    tick();
    const t = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(t); };
  }, [PROMO_BASE]);

  const startEdit = (ch) => {
    setEditingId(ch.id);
    setEditForm({
      display_name: ch.display_name || "",
      source_ip: ch.source_ip || "",
      source_port: ch.source_port ? String(ch.source_port) : "",
      channel_ip: ch.channel_ip || "",
      quality: ch.quality || "SD",
      bandwidth: ch.bandwidth || "",
      channel_number: ch.channel_number ? String(ch.channel_number) : "",
      video_bitrate: ch.video_bitrate ? String(ch.video_bitrate) : "",
      audio1_bitrate: ch.audio1_bitrate ? String(ch.audio1_bitrate) : "",
      audio2_bitrate: ch.audio2_bitrate ? String(ch.audio2_bitrate) : "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async (id) => {
    if (!editForm.display_name?.trim()) {
      setError("Display name is required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.channels.update(id, {
        display_name: editForm.display_name.trim(),
        source_ip: editForm.source_ip.trim() || null,
        source_port: editForm.source_port ? Number(editForm.source_port) : null,
        channel_ip: editForm.channel_ip.trim() || null,
        quality: editForm.quality,
        bandwidth: editForm.bandwidth.trim() || null,
        channel_number: editForm.channel_number
          ? Number(editForm.channel_number)
          : null,
        video_bitrate: editForm.video_bitrate
          ? Number(editForm.video_bitrate)
          : null,
        audio1_bitrate: editForm.audio1_bitrate
          ? Number(editForm.audio1_bitrate)
          : null,
        audio2_bitrate: editForm.audio2_bitrate
          ? Number(editForm.audio2_bitrate)
          : null,
      });
      setEditingId(null);
      setEditForm({});
      setSuccess(`Channel "${id}" updated.`);
      setTimeout(() => setSuccess(null), 3000);
      await load();
    } catch (e) {
      setError(e.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const ef = (k) => (val) => setEditForm((p) => ({ ...p, [k]: val }));

  const handleStatusChange = async (id, newStatus) => {
    await api.channels.updateStatus(id, newStatus);
    await load();
  };

  const handleCreated = async (newId) => {
    setShowNew(false);
    setSuccess(`Channel "${newId}" created successfully.`);
    setTimeout(() => setSuccess(null), 4000);
    await load();
  };

  // ── Promo upload handlers ───────────────────────────────────────────────

  // Called when user clicks "Upload Promo" button on a row
  const handlePromoClick = (ch) => {
    uploadingForChannel.current = { id: ch.id, name: ch.display_name };
    setShowPromoModal(true);
  };

  // Called when a file is chosen — via browse button or drag & drop
  const handleFileSelected = (file) => {
    if (!file) return;
    setShowPromoModal(false);
    startUpload(file);
  };

  const handleModalDrop = (e) => {
    e.preventDefault();
    setPromoDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelected(file);
  };

  // Called from file input (browse button)
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) handleFileSelected(file);
    fileInputRef.current.value = '';
  };

  const startUpload = async (file) => {
    if (!uploadingForChannel.current) return;
    const { id: channelId, name: channelName } = uploadingForChannel.current;
    // Temp key until the server returns the promo guid; then we rekey by guid so
    // the global /promos/active poll can reconcile this entry's status.
    const tempKey = `tmp_${channelId}_${Date.now()}`;

    // Create a local object URL so we can play the video after upload (this
    // session only — the blob is gone after navigation).
    const objectUrl = URL.createObjectURL(file);

    // Add to panel immediately as uploading
    setPromoJobs(prev => ({
      ...prev,
      [tempKey]: {
        channelId,
        channelName,
        filename:  file.name,
        objectUrl,          // kept for local playback
        status:    'uploading',
        message:   null,
      },
    }));
    setPromoPanelOpen(true);
    setPromoPanelMinimized(false);

    try {
      const formData = new FormData();
      formData.append('promo', file);

      const uploadRes = await fetch(`${PROMO_BASE}/channels/${channelId}/promo`, {
        method: 'POST',
        body:   formData,
        headers: authHeaders(),
      });

      if (!uploadRes.ok) {
        const err = await uploadRes.json();
        throw new Error(err.error || 'Upload failed');
      }

      const { job_id, promo_id } = await uploadRes.json();
      const guid = promo_id || job_id;

      // Rekey the optimistic entry to the promo guid as 'processing'. The global
      // poll flips it to success/failed — no per-job timer needed, and the entry
      // survives navigation because it's keyed by the server's promo id.
      setPromoJobs(prev => {
        const next = { ...prev };
        const entry = next[tempKey] || { channelId, channelName, filename: file.name, objectUrl };
        delete next[tempKey];
        next[guid] = { ...entry, promoId: guid, status: 'processing', message: null };
        return next;
      });

    } catch (err) {
      setPromoJobs(prev => ({
        ...prev,
        [tempKey]: { ...prev[tempKey], status: 'failed', message: err.message },
      }));
    }
  };

  // Remove a single job from the panel
  const [playingJob, setPlayingJob] = useState(null); // job to show in video modal

  const dismissJob = (jobKey) => {
    if (pollTimers.current[jobKey]) {
      clearInterval(pollTimers.current[jobKey]);
      delete pollTimers.current[jobKey];
    }
    setPromoJobs(prev => {
      const job = prev[jobKey];
      if (job?.objectUrl) URL.revokeObjectURL(job.objectUrl);
      // Remember the promo id so the active-promos poll doesn't resurrect it
      // (e.g. a still-listed recent failure).
      if (job?.promoId) dismissedIds.current.add(job.promoId);
      const next = { ...prev };
      delete next[jobKey];
      return next;
    });
  };

  const filtered = channels.filter(
    (ch) =>
      urlFilter === "" ||
      ch.display_name?.toLowerCase().includes(urlFilter.toLowerCase()) ||
      ch.source_ip?.includes(urlFilter) ||
      ch.id?.includes(urlFilter),
  );

  const sorted = [...filtered].sort((a, b) => {
    const getNum = (id) => {
      const match = id.match(/\d+$/); // extract number at end
      return match ? Number(match[0]) : 0;
    };

    return getNum(a.id) - getNum(b.id);
  });

  if (showNew)
    return (
      <NewChannelPage
        onBack={() => setShowNew(false)}
        onCreated={handleCreated}
      />
    );

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 18,
          paddingBottom: 14,
          borderBottom: `1px solid var(--border)`,
        }}
      >
        <h1 style={{ fontSize: 17, fontWeight: 700, color: "var(--ink)" }}>
          Channel Management
        </h1>
        {!readOnly && (
          <Btn primary onClick={() => setShowNew(true)}>
            + New Channel
          </Btn>
        )}
      </div>

      {success && (
        <div
          style={{
            background: "var(--green-bg)",
            border: `1px solid var(--green-border)`,
            borderRadius: 7,
            padding: "10px 14px",
            marginBottom: 14,
            fontSize: 12,
            color: "var(--green)",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <span>{success}</span>
          <button
            onClick={() => setSuccess(null)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "var(--green)",
              fontSize: 14,
            }}
          >
            ×
          </button>
        </div>
      )}
      {error && (
        <div
          style={{
            background: "var(--red-bg)",
            border: `1px solid var(--red-border)`,
            borderRadius: 7,
            padding: "10px 14px",
            marginBottom: 14,
            fontSize: 12,
            color: "var(--red)",
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div
          style={{
            fontSize: 12,
            color: "var(--text-dim)",
            padding: "40px 0",
            textAlign: "center",
          }}
        >
          Loading channels…
        </div>
      ) : (
        <div
          style={{
            background: "var(--surface)",
            border: `1px solid var(--border)`,
            borderRadius: 9,
            overflow: "hidden",
          }}
        >
          {/* Filter bar */}
          <div
            style={{
              padding: "10px 14px",
              borderBottom: `1px solid var(--border)`,
              display: "flex",
              alignItems: "center",
              gap: 10,
              background: "var(--surface-alt)",
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "var(--text-dim)",
                textTransform: "uppercase",
                letterSpacing: "0.07em",
                whiteSpace: "nowrap",
              }}
            >
              Filter
            </span>
            <input
              value={urlFilter}
              onChange={(e) => setUrlFilter(e.target.value)}
              placeholder="Search by name, ID or source IP…"
              style={{
                flex: 1,
                background: "var(--surface)",
                border: `1px solid var(--border)`,
                borderRadius: 5,
                padding: "5px 9px",
                fontSize: 12,
                color: "var(--text)",
                fontFamily: "'IBM Plex Mono', monospace",
                outline: "none",
              }}
            />
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>

                <TH>Ch No.</TH>
                <TH>Display Name</TH>
                <TH>Source IP</TH>
                <TH>Port</TH>
                <TH>Quality</TH>
                <TH>Video Bitrate</TH>
                {/* <TH>Audio 1</TH>
                <TH>Audio 2</TH> */}
                <TH>Affiliates</TH>
                <TH>Devices</TH>
                <TH>Promos</TH>
                <TH>Status</TH>
                <TH>Actions</TH>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={11}
                    style={{
                      padding: "28px 14px",
                      textAlign: "center",
                      fontSize: 12,
                      color: "var(--text-dim)",
                    }}
                  >
                    {channels.length === 0
                      ? "No channels yet."
                      : "No channels match the filter."}
                  </td>
                </tr>
              ) : (
                sorted.map((ch) => {
                  const isEditing = editingId === ch.id;
                  return (
                    <tr
                      key={ch.id}
                      style={{
                        background: isEditing
                          ? "var(--surface-alt)"
                          : "transparent",
                      }}
                    >
                      
                      <TD>
                        <Pill label={ch.id} />
                      </TD>

                      <TD>
                        {isEditing ? (
                          <EditCell
                            value={editForm.display_name}
                            onChange={ef("display_name")}
                          />
                        ) : (
                          <span style={{ fontWeight: 500 }}>
                            {ch.display_name}
                          </span>
                        )}
                      </TD>

                      <TD>
                        {isEditing ? (
                          <EditCell
                            value={editForm.source_ip}
                            onChange={ef("source_ip")}
                            mono
                          />
                        ) : (
                          <span
                            style={{
                              fontSize: 12,
                              color: "var(--text-sub)",
                              fontFamily: "'IBM Plex Mono', monospace",
                            }}
                          >
                            {ch.source_ip || "—"}
                          </span>
                        )}
                      </TD>

                      {/* Port */}
                      <TD>
                        {isEditing ? (
                          <EditCell
                            value={editForm.source_port}
                            onChange={ef("source_port")}
                            mono
                          />
                        ) : (
                          <span
                            style={{
                              fontSize: 12,
                              color: "var(--text-sub)",
                              fontFamily: "'IBM Plex Mono', monospace",
                            }}
                          >
                            {ch.source_port || "—"}
                          </span>
                        )}
                      </TD>
                      <TD>
                        {isEditing ? (
                          <div
                            style={{
                              display: "flex",
                              border: `1px solid var(--border)`,
                              borderRadius: 5,
                              overflow: "hidden",
                              width: 80,
                            }}
                          >
                            {["SD", "HD"].map((q) => (
                              <button
                                key={q}
                                onClick={() =>
                                  setEditForm((p) => ({ ...p, quality: q }))
                                }
                                style={{
                                  flex: 1,
                                  padding: "4px 0",
                                  fontSize: 11,
                                  fontWeight:
                                    editForm.quality === q ? 600 : 400,
                                  background:
                                    editForm.quality === q
                                      ? "var(--ink)"
                                      : "var(--surface)",
                                  color:
                                    editForm.quality === q
                                      ? "#fff"
                                      : "var(--text-sub)",
                                  border: "none",
                                  cursor: "pointer",
                                  fontFamily: "inherit",
                                }}
                              >
                                {q}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <span
                            style={{
                              background:
                                ch.quality === "HD"
                                  ? "var(--blue-bg)"
                                  : "var(--surface-alt)",
                              color:
                                ch.quality === "HD"
                                  ? "var(--blue)"
                                  : "var(--text-sub)",
                              border: `1px solid ${ch.quality === "HD" ? "var(--blue-border)" : "var(--border)"}`,
                              borderRadius: 4,
                              padding: "2px 7px",
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            {ch.quality || "SD"}
                          </span>
                        )}
                      </TD>

                      <TD>
                        {isEditing ? (
                          <EditCell
                            value={editForm.video_bitrate}
                            onChange={ef("video_bitrate")}
                            mono
                          />
                        ) : (
                          <span
                            style={{
                              fontSize: 12,
                              color: T.textSub,
                              fontFamily: "'IBM Plex Mono', monospace",
                            }}
                          >
                            {ch.video_bitrate
                              ? `${ch.video_bitrate} Mbps`
                              : "—"}
                          </span>
                        )}
                      </TD>

                      {/* <TD>
                        {isEditing ? (
                          <EditCell
                            value={editForm.audio1_bitrate}
                            onChange={ef("audio1_bitrate")}
                            mono
                          />
                        ) : (
                          <span
                            style={{
                              fontSize: 12,
                              color: T.textSub,
                              fontFamily: "'IBM Plex Mono', monospace",
                            }}
                          >
                            {ch.audio1_bitrate
                              ? `${ch.audio1_bitrate} Kbps`
                              : "—"}
                          </span>
                        )}
                      </TD>

                      <TD>
                        {isEditing ? (
                          <EditCell
                            value={editForm.audio2_bitrate}
                            onChange={ef("audio2_bitrate")}
                            mono
                          />
                        ) : (
                          <span
                            style={{
                              fontSize: 12,
                              color: T.textSub,
                              fontFamily: "'IBM Plex Mono', monospace",
                            }}
                          >
                            {ch.audio2_bitrate
                              ? `${ch.audio2_bitrate} Kbps`
                              : "—"}
                          </span>
                        )}
                      </TD> */}


                      <TD sub>{ch.affiliates || 0}</TD>
                      <TD sub>{ch.devices || 0}</TD>
                      <TD>
                        {(() => {
                          const count = promoCounts[ch.id] || 0;
                          return count > 0 ? (
                            <button
                              onClick={() => onOpenPromo(ch, true)}
                              title="View promos"
                              style={{
                                background: "var(--blue-bg, #eff6ff)",
                                border: "1px solid var(--blue-border, #bfdbfe)",
                                borderRadius: 12,
                                padding: "3px 10px",
                                fontSize: 11,
                                fontWeight: 600,
                                color: "var(--blue, #2563eb)",
                                cursor: "pointer",
                                fontFamily: "inherit",
                              }}
                            >
                              {count} promo{count !== 1 ? "s" : ""}
                            </button>
                          ) : (
                            <span
                              title="Add at least 1 promo"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 22,
                                height: 22,
                                borderRadius: "50%",
                                background: "var(--amber-bg, #fffbeb)",
                                border: "1px solid var(--amber-border, #fcd34d)",
                                color: "var(--amber, #d97706)",
                                fontSize: 13,
                                cursor: "default",
                                flexShrink: 0,
                              }}
                            >
                              ⚠
                            </span>
                          );
                        })()}
                      </TD>

                      

                      {/* Status */}
                      <TD>
                        {isEditing || readOnly ? (
                          <Badge status={ch.status} />
                        ) : (
                          <StatusToggle
                            id={ch.id}
                            currentStatus={ch.status}
                            onConfirm={handleStatusChange}
                          />
                        )}
                      </TD>

                      {/* Actions */}
                      <TD>
                        {isEditing ? (
                          <div style={{ display: "flex", gap: 6 }}>
                            <button
                              onClick={() => saveEdit(ch.id)}
                              disabled={saving}
                              style={{
                                background: "var(--ink)",
                                border: "none",
                                borderRadius: 5,
                                padding: "4px 10px",
                                fontSize: 11,
                                fontWeight: 600,
                                color: "#fff",
                                cursor: saving ? "not-allowed" : "pointer",
                                fontFamily: "inherit",
                              }}
                            >
                              {saving ? "…" : "Save"}
                            </button>
                            <button
                              onClick={cancelEdit}
                              style={{
                                background: "var(--surface)",
                                border: `1px solid var(--border)`,
                                borderRadius: 5,
                                padding: "4px 10px",
                                fontSize: 11,
                                color: "var(--text-sub)",
                                cursor: "pointer",
                                fontFamily: "inherit",
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (() => {
                          // Find the latest successful promo job for this channel
                          const successJob = Object.values(promoJobs)
                            .filter(j => j.channelId === ch.id && j.status === 'success' && j.objectUrl)
                            .at(-1);
                          return (
                          <div style={{ display: "flex", gap: 6 }}>
                            <button
                              onClick={() => setViewingChannel(ch)}
                              title="View details"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                                background: "var(--surface)",
                                border: `1px solid var(--border)`,
                                borderRadius: 5,
                                padding: "4px 10px",
                                fontSize: 11,
                                color: "var(--text-sub)",
                                cursor: "pointer",
                                fontFamily: "inherit",
                              }}
                            >
                              <Eye size={13} />
                            </button>
                            {!readOnly && (
                              <button
                                onClick={() => startEdit(ch)}
                                title="Edit channel"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 5,
                                  background: "var(--surface)",
                                  border: `1px solid var(--border)`,
                                  borderRadius: 5,
                                  padding: "4px 10px",
                                  fontSize: 11,
                                  color: "var(--text-sub)",
                                  cursor: "pointer",
                                  fontFamily: "inherit",
                                }}
                              >
                                <Pencil size={13} />
                              </button>
                            )}
                            <button
                              onClick={() => onOpenPromo(ch, false)}
                              style={{
                                background: "var(--blue-bg, #eff6ff)",
                                border: `1px solid var(--blue-border, #bfdbfe)`,
                                borderRadius: 5,
                                padding: "4px 10px",
                                fontSize: 11,
                                color: "var(--blue, #2563eb)",
                                cursor: "pointer",
                                fontFamily: "inherit",
                                fontWeight: 500,
                                whiteSpace: "nowrap",
                              }}
                            >
                              ↑ Promo
                            </button>
                            {successJob && (
                              <button
                                onClick={() => setPlayingJob(successJob)}
                                title="Play uploaded promo"
                                style={{
                                  background: "var(--green-bg, #f0fdf4)",
                                  border: `1px solid var(--green-border, #86efac)`,
                                  borderRadius: 5,
                                  padding: "4px 10px",
                                  fontSize: 13,
                                  color: "var(--green, #16a34a)",
                                  cursor: "pointer",
                                  lineHeight: 1,
                                }}
                              >
                                ▶
                              </button>
                            )}
                          </div>
                          );
                        })()}
                      </TD>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Promo Upload Modal ── */}
      {showPromoModal && (
        <div
          onClick={() => setShowPromoModal(false)}
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 2000,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "var(--surface)",
              border: `1px solid var(--border)`,
              borderRadius: 14,
              padding: "32px 28px",
              width: 420,
              maxWidth: "90vw",
              boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
              fontFamily: "inherit",
            }}
          >
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)" }}>
                  Upload Promo
                </div>
                <div style={{ fontSize: 12, color: "var(--text-sub)", marginTop: 3 }}>
                  {uploadingForChannel.current?.name}
                </div>
              </div>
              <button
                onClick={() => setShowPromoModal(false)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: "var(--text-dim)", fontSize: 20, lineHeight: 1, padding: 0,
                }}
              >×</button>
            </div>

            {/* Drag & Drop Zone */}
            <div
              onDragOver={e => { e.preventDefault(); setPromoDragOver(true); }}
              onDragLeave={() => setPromoDragOver(false)}
              onDrop={handleModalDrop}
              onClick={() => fileInputRef.current.click()}
              style={{
                border: `2px dashed ${promoDragOver ? "var(--ink)" : "var(--border)"}`,
                borderRadius: 10,
                padding: "36px 20px",
                textAlign: "center",
                cursor: "pointer",
                background: promoDragOver ? "var(--surface-alt)" : "transparent",
                transition: "all 0.15s",
                marginBottom: 16,
              }}
            >
              {/* Upload icon */}
              <div style={{ fontSize: 36, marginBottom: 10, lineHeight: 1 }}>📁</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", marginBottom: 5 }}>
                {promoDragOver ? "Drop to upload" : "Drag & drop your video here"}
              </div>
              <div style={{ fontSize: 12, color: "var(--text-dim)", marginBottom: 16 }}>
                or
              </div>
              <div
                style={{
                  display: "inline-block",
                  background: "var(--ink)",
                  color: "#fff",
                  borderRadius: 7,
                  padding: "8px 20px",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Browse file
              </div>
              <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 14 }}>
                MP4, MOV, AVI, MKV, WEBM &nbsp;·&nbsp; Max 600 MB
              </div>
            </div>

            <button
              onClick={() => setShowPromoModal(false)}
              style={{
                width: "100%",
                background: "none",
                border: `1px solid var(--border)`,
                borderRadius: 7,
                padding: "8px 0",
                fontSize: 12,
                color: "var(--text-sub)",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Hidden file input — triggered by Upload Promo button */}
      <input
        ref={fileInputRef}
        type="file"
        accept="video/mp4,video/quicktime,video/x-msvideo,video/x-matroska,video/webm"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {/* ── Google Drive-style floating upload panel ── */}
      {hasPromoJobs && promoPanelOpen && (
        <div
          style={{
            position: "fixed",
            bottom: 24,
            right: 24,
            width: 340,
            background: "var(--surface)",
            border: `1px solid var(--border)`,
            borderRadius: 10,
            boxShadow: "0 4px 24px rgba(0,0,0,0.13)",
            zIndex: 1000,
            overflow: "hidden",
            fontFamily: "inherit",
          }}
        >
          {/* Panel header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 14px",
              background: "var(--ink)",
              color: "#fff",
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600 }}>
              Promo Uploads ({Object.keys(promoJobs).length})
            </span>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button
                onClick={() => setPromoPanelMinimized(p => !p)}
                title={promoPanelMinimized ? "Expand" : "Minimise"}
                style={{
                  background: "none",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: 16,
                  lineHeight: 1,
                  padding: "0 2px",
                }}
              >
                {promoPanelMinimized ? "▲" : "▼"}
              </button>
              <button
                onClick={() => {
                  // Always close the panel; active jobs keep polling in background
                  // and will reopen the panel when they complete
                  setPromoPanelOpen(false);
                  // Revoke objectURLs and clear only finished jobs
                  setPromoJobs(prev => {
                    const next = {};
                    Object.entries(prev).forEach(([k, v]) => {
                      if (v.status === 'uploading' || v.status === 'processing') {
                        next[k] = v; // keep active jobs running silently
                      } else {
                        if (v.objectUrl) URL.revokeObjectURL(v.objectUrl);
                      }
                    });
                    return next;
                  });
                }}
                title="Close"
                style={{
                  background: "none",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: 18,
                  lineHeight: 1,
                  padding: "0 2px",
                }}
              >
                ×
              </button>
            </div>
          </div>

          {/* Job list */}
          {!promoPanelMinimized && (
            <div style={{ maxHeight: 280, overflowY: "auto" }}>
              {Object.entries(promoJobs).map(([jobKey, job]) => (
                <div
                  key={jobKey}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    padding: "10px 14px",
                    borderBottom: `1px solid var(--border)`,
                  }}
                >
                  {/* Status icon */}
                  <div style={{ marginTop: 2, fontSize: 15, flexShrink: 0 }}>
                    {(job.status === 'uploading' || job.status === 'processing') && (
                      <span
                        style={{
                          display: "inline-block",
                          width: 13,
                          height: 13,
                          border: "2px solid var(--border)",
                          borderTopColor: "var(--ink)",
                          borderRadius: "50%",
                          animation: "promo-spin 0.7s linear infinite",
                        }}
                      />
                    )}
                    {job.status === 'success' && (
                      <span style={{ color: "var(--green, #16a34a)", fontWeight: 700 }}>✓</span>
                    )}
                    {job.status === 'failed' && (
                      <span style={{ color: "var(--red, #dc2626)", fontWeight: 700 }}>✕</span>
                    )}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "var(--text)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {job.channelName}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--text-sub)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        marginTop: 1,
                      }}
                    >
                      {job.filename}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        marginTop: 3,
                        color:
                          job.status === 'success' ? "var(--green, #16a34a)"
                          : job.status === 'failed' ? "var(--red, #dc2626)"
                          : "var(--text-dim)",
                        fontStyle:
                          job.status === 'processing' || job.status === 'uploading'
                            ? "italic" : "normal",
                      }}
                    >
                      {job.status === 'uploading'  && "Uploading…"}
                      {job.status === 'processing' && "Processing…"}
                      {job.status === 'success'    && "Upload complete"}
                      {job.status === 'failed'     && (job.message || "Failed")}
                    </div>
                  </div>

                  {/* Actions — only for finished jobs */}
                  {(job.status === 'success' || job.status === 'failed') && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                      {job.status === 'success' && job.objectUrl && (
                        <button
                          onClick={() => setPlayingJob(job)}
                          title="Play promo"
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "var(--ink)",
                            fontSize: 17,
                            lineHeight: 1,
                            padding: 0,
                          }}
                        >
                          ▶
                        </button>
                      )}
                      <button
                        onClick={() => dismissJob(jobKey)}
                        title="Dismiss"
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "var(--text-dim)",
                          fontSize: 16,
                          lineHeight: 1,
                          padding: 0,
                        }}
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Video Player Modal ── */}
      {playingJob && (
        <div
          onClick={() => setPlayingJob(null)}
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.75)",
            zIndex: 3000,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "var(--surface)",
              border: `1px solid var(--border)`,
              borderRadius: 14,
              padding: "20px 20px 16px",
              width: "min(720px, 92vw)",
              boxShadow: "0 12px 48px rgba(0,0,0,0.35)",
              fontFamily: "inherit",
            }}
          >
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)" }}>
                  {playingJob.channelName}
                </div>
                <div style={{ fontSize: 11, color: "var(--text-sub)", marginTop: 2 }}>
                  {playingJob.filename}
                </div>
              </div>
              <button
                onClick={() => setPlayingJob(null)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: "var(--text-dim)", fontSize: 22, lineHeight: 1, padding: 0,
                }}
              >×</button>
            </div>

            {/* Video player */}
            <video
              src={playingJob.objectUrl}
              controls
              autoPlay
              style={{
                width: "100%",
                borderRadius: 8,
                background: "#000",
                display: "block",
                maxHeight: "60vh",
              }}
            />

            <div style={{ marginTop: 10, fontSize: 11, color: "var(--text-dim)", textAlign: "center" }}>
              Local preview — server-side processing may still be underway
            </div>
          </div>
        </div>
      )}

      {viewingChannel && (
        <ChannelDetailsModal
          channel={viewingChannel}
          onClose={() => setViewingChannel(null)}
          readOnly={readOnly}
          onSaved={async (updated) => {
            await load();
            setViewingChannel(updated);
          }}
        />
      )}

      <style>{`@keyframes promo-spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}